import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { PageViewport, PDFPageProxy } from 'pdfjs-dist';
import { Mutex } from 'async-mutex';
import { getDownloadURL, getStorage, ref } from '@angular/fire/storage';
import { HttpClient, HttpEventType, HttpResponse } from '@angular/common/http';
import { filter, map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { captureException } from '@sentry/angular';
import { PdfCacheService } from '../../../../services/pdf-cache.service';
import pdfjsLib from '../../../../../assets/pdfjs-config';
import { ItemsCanvasService } from '../../../../services/items-canvas.service';
import { DeviceAndBrowserDetectorService } from '../../../../services/device-and-browser-detector.service';
import { FLAGS, FlagsService } from '../../../../services/flags.service';
import { TelemetryService } from '../../../../services/telemetry.service';
import { PDF_RENDERING_ERROR_EVENT_NAME } from '../../items-canvas/board-items/board-pdf';
import { checkIfValidLibreOfficeType } from '../../../../services/upload-file.service';
import { FirebaseStorageUploadService } from '../../../../services/firebase-storage-upload';
import { environment } from '../../../../../environments/environment';
import { modifiedSetInterval } from '../../../../utilities/ZoneUtils';
import { CdnResourcesSignerService } from '../../../../services/cdn-resources-signer.service';

export const MAX_PDF_PAGES_TO_RENDER = 500;
export const PDF_VIEWPORT_SCALE_FACTOR = 1.4;

export enum PdfProcessingState {
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  COMPLETE = 'complete',
}

export interface PdfProgress {
  progress: number;
  state: PdfProcessingState;
  intervalId?: NodeJS.Timer;
}

export interface PageWithViewport {
  viewport: PageViewport;
  pageProxy: PDFPageProxy;
  height: number;
  width: number;
  shiftFromTop?: number;
  imageUrl?: string;
}
export interface ImagesSizes {
  range: string;
  size: { height: number; width: number };
}

export interface ConversionResponse {
  imagesSizes: ImagesSizes[];
  numberOfPages: number;
  imagesToken: string;
  imagesPrefix: string;
}

const PDF_CONVERSION_ERROR_EVENT_NAME = 'PDF Conversion Error';

@Injectable({
  providedIn: 'root',
})
export class PdfManagerService {
  private readonly pdfLoadingStates = new Map<string, BehaviorSubject<boolean>>();
  private readonly pdfProgress = new Map<string, BehaviorSubject<PdfProgress>>();
  private readonly pdfLoadMutex = new Mutex();
  private readonly signedPdfPageUrls = new Map<string, string[]>();

  constructor(
    private pdfCacheService: PdfCacheService,
    private itemsCanvasService: ItemsCanvasService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private flagsService: FlagsService,
    private telemetryService: TelemetryService,
    private httpClient: HttpClient,
    private cdnResourcesSignerService: CdnResourcesSignerService,
    private firebaseStorageUploadService: FirebaseStorageUploadService,
  ) {}

  private async getPdfPages(
    contentId: string,
    url: string,
  ): Promise<PageWithViewport[] | undefined> {
    // To load the PDF only once, and then we use the pdf from cache for other calls
    // This ensures that in any order and any number of times the pdf manager methods are invoked, only one is fetched from the network and the others from cache
    const release = await this.pdfLoadMutex.acquire();

    this.setPDFLoadingState(contentId, true);

    try {
      return await this._getPages(contentId, url);
    } catch (e) {
      this.telemetryService.errorEvent(PDF_RENDERING_ERROR_EVENT_NAME, {
        error: e,
        contentId,
        url,
        level: 'fetching the PDF',
      });
    } finally {
      release();
      this.setPDFLoadingState(contentId, false);
    }
  }

  private async _getPdf(contentId: string, url: string) {
    let pdfDocumentProxy = this.pdfCacheService.getPDFIfExists(contentId);

    if (!pdfDocumentProxy) {
      const trustedUrl = await this.itemsCanvasService.getTrustedUrl(url);
      pdfDocumentProxy = await pdfjsLib.getDocument({
        url: trustedUrl,
        cMapUrl: `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
        cMapPacked: true,
      }).promise;
    }

    this.pdfCacheService.cachePDF(contentId, pdfDocumentProxy);

    return pdfDocumentProxy;
  }

  private async _getPages(contentId: string, url: string) {
    const pdfDocumentProxy = await this._getPdf(contentId, url);
    const numberOfPagesToRender = Math.min(
      pdfDocumentProxy.numPages as number,
      MAX_PDF_PAGES_TO_RENDER,
    );

    const promises: Promise<PageWithViewport>[] = [];
    for (let i = 0; i < numberOfPagesToRender; i++) {
      const cachedPageWithViewport = this.pdfCacheService.getPageIfExists(contentId, i);

      if (cachedPageWithViewport) {
        promises.push(Promise.resolve(cachedPageWithViewport));
        continue;
      }

      const pageProxyPromise = pdfDocumentProxy.getPage(i + 1);
      // Using .then is better than awaiting even if it increases indentation, because this way we are triggering each action without waiting the other to finish
      const pagePromise = pageProxyPromise.then((pageProxy) => {
        const viewport = pageProxy.getViewport({ scale: this.scaleForPdfPages });
        const scaledWidth = Math.floor(viewport.width / this.scaleFactor);
        const scaledHeight = Math.floor(viewport.height / this.scaleFactor);
        return {
          pageProxy,
          viewport,
          height: scaledHeight,
          width: scaledWidth,
        };
      });

      promises.push(pagePromise);
    }

    let totalHeightOfPages = 0;
    for (let i = 0; i < numberOfPagesToRender; i++) {
      const pageWithViewPort = await promises[i];
      pageWithViewPort.shiftFromTop = totalHeightOfPages;

      this.pdfCacheService.cachePage(contentId, i, pageWithViewPort);

      totalHeightOfPages += pageWithViewPort.height;
    }

    return Promise.all(promises);
  }

  private setPDFLoadingState(contentId: string, isLoading: boolean) {
    this.emitToObservableInMap(this.pdfLoadingStates, contentId, isLoading);
  }

  private emitToObservableInMap(
    mapOfObservables: Map<string, BehaviorSubject<boolean>>,
    key: string,
    isLoading: boolean,
  ) {
    const subject = mapOfObservables.get(key);
    if (subject) {
      subject.next(isLoading);
    } else {
      mapOfObservables.set(key, new BehaviorSubject(isLoading));
    }
  }

  private get scaleForPdfPages(): number {
    if (this.deviceAndBrowserDetectorService.isiOSiPhone()) {
      return (
        (this.flagsService.featureFlagsVariables.enable_pdf_upscale
          .pdf_scale_factor_mobile_ios as number) || 1
      );
    }
    if (this.deviceAndBrowserDetectorService.isAndroid()) {
      return (
        (this.flagsService.featureFlagsVariables.enable_pdf_upscale
          .pdf_scale_factor_mobile_android as number) || 1.2
      );
    }
    return this.scaleFactor * PDF_VIEWPORT_SCALE_FACTOR;
  }

  private get scaleFactor() {
    return this.flagsService.isFlagEnabled(FLAGS.ENABLE_PDF_UPSCALE)
      ? (this.flagsService.featureFlagsVariables.enable_pdf_upscale.pdf_scale_factor as number)
      : 1.0;
  }

  public async getPdfSize(contentId: string, url: string) {
    const pages = (await this.getPdfPages(contentId, url)) ?? [];

    const width = Math.max(...pages.map((p) => p.width));
    const height = Math.max(...pages.map((p) => (p.shiftFromTop ?? 0) + p.height));

    return { width, height };
  }

  public async getConvertedImages(contentId: string) {
    return this.pdfCacheService.getConvertedImagesIfExists(contentId);
  }

  public async getPagesWithViewport(contentId: string, url: string) {
    return await this.getPdfPages(contentId, url);
  }

  public async getPageWithViewport(contentId: string, url: string, index: number) {
    const pages = await this.getPdfPages(contentId, url);
    return pages ? pages[index] : undefined;
  }

  public getPdfLoadingState(contentId: string) {
    let subject = this.pdfLoadingStates.get(contentId);
    if (!subject) {
      subject = new BehaviorSubject<boolean>(true);
      this.pdfLoadingStates.set(contentId, subject);
    }
    return subject;
  }

  public getPdfUploadProgress(contentId: string) {
    let subject = this.pdfProgress.get(contentId);
    if (!subject) {
      subject = new BehaviorSubject<PdfProgress>({
        progress: 0,
        state: PdfProcessingState.UPLOADING,
      });
      this.pdfProgress.set(contentId, subject);
    }
    return subject.pipe(map((progress) => progress.progress));
  }

  public getPdfProcessingState(contentId: string) {
    let subject = this.pdfProgress.get(contentId);
    if (!subject) {
      subject = new BehaviorSubject<PdfProgress>({
        progress: 0,
        state: PdfProcessingState.UPLOADING,
      });
      this.pdfProgress.set(contentId, subject);
    }
    return subject.pipe(map((progress) => progress.state));
  }

  private updatePdfProgress(
    contentId: string,
    progress?: number,
    state?: PdfProcessingState,
    intervalId?: NodeJS.Timer,
  ) {
    const subject = this.pdfProgress.get(contentId);
    if (subject) {
      const currentValue = subject.value;
      subject.next({
        progress: progress ?? currentValue.progress,
        state: state ?? currentValue.state,
        intervalId,
      });
    }
  }

  private updatePdfState(contentId: string, state: PdfProcessingState) {
    const subject = this.pdfProgress.get(contentId);
    if (subject) {
      const currentValue = subject.value;
      subject.next({
        progress: currentValue.progress,
        state,
      });

      if (state === PdfProcessingState.COMPLETE && currentValue.intervalId) {
        clearInterval(currentValue.intervalId);
        subject.complete();
        this.pdfProgress.delete(contentId);
      }
    }
  }

  public async uploadPdfAsImages(file: File, contentId: string) {
    this.setPDFLoadingState(contentId, true);
    this.updatePdfProgress(contentId, 0);
    this.updatePdfState(contentId, PdfProcessingState.UPLOADING);

    const isOfficeFile = checkIfValidLibreOfficeType(file);
    this.startSimulatingProgress(contentId, file, isOfficeFile);

    let fileRef;
    try {
      const storage = getStorage();
      const fileId = uuid();
      fileRef = ref(storage, `pdf-uploads/${fileId}`);
      const uploadTask = this.firebaseStorageUploadService.uploadBytesResumable(fileRef, file);

      await uploadTask;

      const downloadUrl = await this.resolveDownloadUrl(fileRef);

      const fileType = isOfficeFile ? 'office' : 'pdf';

      const form = new FormData();
      form.append('url', downloadUrl);
      form.append('scaleForPdfPages', this.scaleForPdfPages.toString());
      form.append('scaleFactor', this.scaleFactor.toString());
      form.append('fileType', fileType);

      const response: any = await firstValueFrom(
        this.httpClient
          .post(environment.pdfConverterServer, form, {
            reportProgress: true,
            observe: 'events',
          })
          .pipe(
            filter((event) => event.type === HttpEventType.Response),
            map((event) => event as HttpResponse<any>),
            map((r) => r.body),
          ),
      );

      if (!this.validateImagesResponse(response)) {
        throw new Error('invalid response from converter');
      }

      this.pdfCacheService.cacheConvertedImages(contentId, response);
      await this.preSignPdfPageUrls(response);
      this.setPDFLoadingState(contentId, false);

      return true;
    } catch (e) {
      this.telemetryService.errorEvent(PDF_CONVERSION_ERROR_EVENT_NAME, e.message);
      return;
    } finally {
      this.updatePdfState(contentId, PdfProcessingState.COMPLETE);
    }
  }

  private async resolveDownloadUrl(fileRef: ReturnType<typeof ref>): Promise<string> {
    if (!this.flagsService.isFlagEnabled(FLAGS.ENABLE_CDN_RESOURCES_SIGNING)) {
      return getDownloadURL(fileRef);
    }

    try {
      return await this.cdnResourcesSignerService.getSignedUrl(fileRef.fullPath);
    } catch (error) {
      return getDownloadURL(fileRef);
    }
  }

  private validateImagesResponse(response: any): response is ConversionResponse {
    if (!response) {
      return false;
    }
    const numberOfPages = response.numberOfPages;
    if (numberOfPages === undefined || typeof numberOfPages !== 'number') {
      return false;
    }

    const imagesToken = response.imagesToken;
    if (imagesToken === undefined || typeof imagesToken !== 'string') {
      return false;
    }

    const imagesPrefix = response.imagesPrefix;
    if (imagesPrefix === undefined || typeof imagesPrefix !== 'string') {
      return false;
    }

    const imagesSizes = response.imagesSizes;
    if (!Array.isArray(imagesSizes)) {
      return false;
    }

    for (const image of imagesSizes) {
      if (
        typeof image?.range !== 'string' ||
        typeof image?.size?.width !== 'number' ||
        typeof image?.size?.height !== 'number'
      ) {
        return false;
      }
    }
    return true;
  }

  private async startSimulatingProgress(contentId: string, file: File, isOfficeFile = false) {
    let numberOfPages: number = 50; // default in case it is an office file, or we could not read it
    if (!isOfficeFile) {
      try {
        const fileAsBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: fileAsBuffer }).promise;
        numberOfPages = pdf.numPages;
      } catch (e) {
        captureException(e);
      }
    }

    const sizeInMb: number = file.size / (1024 * 1024);

    // This equation was concluded after trying different files and estimating the time taken by the converter
    const estimatedTimeSec =
      3.333 + 0.0955 * numberOfPages + 0.666 * sizeInMb + (isOfficeFile ? 10 : 0);

    this.simulateProgress(estimatedTimeSec, (progress) => {
      // Base progress = 25% which is the time taken to upload the file
      this.updatePdfProgress(contentId, progress);
      if (progress >= 100) {
        this.updatePdfState(contentId, PdfProcessingState.PROCESSING);
      }
    });
  }

  simulateProgress(estimatedTimeSec: number, onUpdate: (progress: number) => void) {
    const totalMs = estimatedTimeSec * 1000;
    const intervalMs = 100; // update every 100ms
    const steps = totalMs / intervalMs;
    let currentStep = 0;

    const progressInterval = modifiedSetInterval(() => {
      currentStep++;
      let progress = (currentStep / steps) * 100;

      // Optionally slow down last 5% to give smoother feel
      if (progress > 95) {
        progress = 95 + (progress - 95) * 0.2;
      }

      onUpdate(Math.min(progress, 100));

      if (currentStep >= steps) {
        clearInterval(progressInterval);
        onUpdate(100);
      }
    }, intervalMs);
  }

  getImageUrl(pageNum: number, imagesPrefix?: string, imagesToken?: string) {
    const signedUrls = imagesPrefix ? this.signedPdfPageUrls.get(imagesPrefix) : undefined;
    if (signedUrls?.[pageNum]) {
      return signedUrls[pageNum];
    }
    return `${environment.pdfConversionsFolder}%2F${imagesPrefix}-${pageNum}.jpeg?alt=media&token=${imagesToken}`;
  }

  private async preSignPdfPageUrls(response: ConversionResponse): Promise<void> {
    if (!this.flagsService.isFlagEnabled(FLAGS.ENABLE_CDN_RESOURCES_SIGNING)) {
      return;
    }

    try {
      const paths = Array.from(
        { length: response.numberOfPages },
        (_, i) => `pdf-conversions/${response.imagesPrefix}-${i}.jpeg`,
      );
      const signedUrls = await this.cdnResourcesSignerService.getSignedUrls(paths);
      this.signedPdfPageUrls.set(response.imagesPrefix, signedUrls);
    } catch (error) {
      // Fall back to Firebase token URLs silently
    }
  }

  decompressImagesSizes(conversionResponse: ConversionResponse) {
    // The images sizes are stored in this format:
    // [
    //  { range: "a-b" , size: { width: number, height: number } } ,
    //  { range: "a+1-c" , size: { width: number, height: number } } ,
    //  { range: "c+1-d" , size: { width: number, height: number } } ,
    //     ....
    // ]
    // Which is a more compressed representation of storing the size of each page, which will reduce the memory consumption,
    // That's because usually all/most the PDF pages are the same size, and this compression will probably happen.
    // In this function we decompress them to a simpler form which is
    // [
    //  { width: number, height: number },
    //  { width: number, height: number },
    //  { width: number, height: number },
    // ]
    // Where there is a size entry for each page in the PDF.
    const decompressedImagesSizes: { width: number; height: number }[] = [];
    let rangeIndex = 0;
    if (conversionResponse.imagesSizes.length === 0) {
      return [];
    }

    let currentRangeKey;
    let currentRangeStart;
    let currentRangeEnd;
    let currentRangeSize;

    const initRangeVars = (i) => {
      currentRangeKey = conversionResponse.imagesSizes[i].range;
      currentRangeStart = +currentRangeKey.split('-')[0];
      currentRangeEnd = +currentRangeKey.split('-')[1];
      currentRangeSize = conversionResponse.imagesSizes[i].size;
    };

    initRangeVars(rangeIndex);

    for (let pageNum = 1; pageNum <= conversionResponse.numberOfPages; pageNum++) {
      if (currentRangeStart <= pageNum && pageNum <= currentRangeEnd) {
        decompressedImagesSizes.push(currentRangeSize);
      } else {
        pageNum--; // to try again;
        rangeIndex++; // to try the next range
        initRangeVars(rangeIndex);
      }
    }
    return decompressedImagesSizes;
  }
}
